/**
 * DashboardWidgets — spec iter282 (2026-09-02) : cartes module (anneau %,
 * sélecteur S/M/A, compteur, courbe de tendance), bande « à venir »,
 * tableau « Attention requise » (filtres pilules, tri/filtre colonne,
 * lignes cliquables, zone scrollable ~190 px).
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";
const TooltipArrow = TooltipPrimitive.Arrow;
import { AlertTriangle, ChevronDown, Paperclip, Users, ListChecks, FileText, CheckCircle2, Mail, Info, X, Contact, Clock, HeartPulse, TrendingUp, TrendingDown, Cloud, Server, FileCode, Copy, Globe, Cpu, CircleCheck, TriangleAlert, OctagonAlert, Layers, UserCheck, DatabaseZap } from "lucide-react";
import { toast } from "@/lib/toast";
import { useNavigate } from "@/hooks/useDeskNavigate";
import { scoreColor, PerformanceCard } from "@/components/PerformanceIndex";
import api from "@/lib/api";
import { avatarColorForSeed } from "@/lib/avatarColors";
import UserAvatar from "@/components/ui/UserAvatar";
import { useUserDirectory } from "@/lib/userDirectory";
import QuickViewDialog from "@/components/common/QuickViewDialog";

export const DASH_MODULES = [
  { key: "taches",      label: "Tâches",      color: "#F9BC26", ref: "#F9BC26", pillBg: "#3f321a", icon: ListChecks },
  { key: "documents",   label: "Documents",   color: "#7bb3e8", ref: "#3568B0", pillBg: "#1a2a3a", icon: FileText },
  { key: "accuses",     label: "Accusé",      color: "#F9A826", ref: "#F9A826", pillBg: "#3a2a1a", icon: Mail },
  { key: "validations", label: "Validation",  color: "#8a8fe0", ref: "#8a8fe0", pillBg: "#2a2438", icon: CheckCircle2 },
  { key: "information", label: "Information", color: "#2a9a7a", ref: "#2a9a7a", pillBg: "#1a3328", icon: Info },
];
const COMING = [{ key: "crm", label: "Commercial", icon: Contact }, { key: "pointage", label: "Pointage", icon: Clock }];
export { COMING as DASH_COMING };
const PERIODS = [["week", "S", "Semaine"], ["month", "M", "Mois"], ["year", "A", "Année"]];
const DAYS = ["Di", "Lu", "Ma", "Me", "Je", "Ve", "Sa"];
const MONTHS = ["Jan", "Fév", "Mar", "Avr", "Mai", "Juin", "Juil", "Aoû", "Sep", "Oct", "Nov", "Déc"];

function fmtLabel(label, period) {
  if (period === "month") return label;
  const d = new Date(`${label}T00:00:00`);
  if (period === "week") return DAYS[d.getDay()];
  return MONTHS[d.getMonth()];
}

/** iter161 P2 — mode compact (≤ 1440 px) : anneau KPI réduit. */
function useCompact() {
  const [c, setC] = useState(() => typeof window !== "undefined" && window.matchMedia("(max-width: 1440px)").matches);
  useEffect(() => { const mq = window.matchMedia("(max-width: 1440px)"); const h = (e) => setC(e.matches); mq.addEventListener("change", h); return () => mq.removeEventListener("change", h); }, []);
  return c;
}

function PctRing({ value, color, size = 44 }) {
  const r = (size - 5) / 2, c = 2 * Math.PI * r;
  const v = value == null ? 0 : Math.max(0, Math.min(100, value));
  return (
    <div className="relative shrink-0" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#3a3a3e" strokeWidth="3.5" />
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth="3.5" strokeLinecap="round"
          strokeDasharray={c} strokeDashoffset={c - (v / 100) * c} style={{ transition: "stroke-dashoffset .6s ease" }} />
      </svg>
      <span className="absolute inset-0 flex items-center justify-center text-[12px] font-bold tabular-nums" style={{ color }}
        data-testid="dash-card-pct">
        {value == null ? "—" : value}
      </span>
    </div>
  );
}

function TrendChart({ series, period, color, id, height = null }) {
  const { labels = [], values = [], current_index = 0 } = series?.[period] || {};
  const W = 220, H = 56, P = 4;
  const max = Math.max(1, ...values);
  const n = Math.max(1, values.length - 1);
  const pts = values.map((v, i) => [P + (i / n) * (W - 2 * P), H - P - (v / max) * (H - 2 * P)]);
  const line = pts.map((p, i) => `${i ? "L" : "M"}${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(" ");
  const area = pts.length ? `${line} L${pts[pts.length - 1][0].toFixed(1)},${H} L${pts[0][0].toFixed(1)},${H} Z` : "";
  const last = pts[pts.length - 1];
  const step = period === "year" ? Math.ceil(labels.length / 6) : 1;
  return (
    <div data-testid={`dash-trend-${id}`}>
      <svg viewBox={`0 0 ${W} ${H}`} className={height ? "w-full" : "w-full h-14"} style={height ? { height } : undefined} preserveAspectRatio="none">
        <defs>
          <linearGradient id={`g-${id}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity="0.3" />
            <stop offset="100%" stopColor={color} stopOpacity="0" />
          </linearGradient>
        </defs>
        {area && <path d={area} fill={`url(#g-${id})`} />}
        {line && <path d={line} fill="none" stroke={color} strokeWidth="1.6" vectorEffect="non-scaling-stroke" strokeLinejoin="round" />}
        {last && <circle cx={last[0]} cy={last[1]} r="3" fill={color} />}
      </svg>
      {!height && <div className="flex justify-between mt-1 text-[9.5px] text-[#8a8a8f] tabular-nums">
        {labels.map((l, i) => (
          <span key={l} className="flex-1 text-center" style={{
            visibility: i % step === 0 || i === current_index ? "visible" : "hidden",
            color: i === current_index ? color : undefined, fontWeight: i === current_index ? 700 : 400 }}>
            {fmtLabel(l, period)}
          </span>
        ))}
      </div>}
    </div>
  );
}

export function ModuleCard({ mod, data, pct, period, onPeriod }) {
  const compact = useCompact();
  const urgent = data?.urgent || { label: "—", level: "ok" };
  const urgentColor = urgent.level === "danger" ? "#f87171" : urgent.level === "warn" ? "#f0a868" : "#5dcaa5";
  return (
    <div className="rounded-xl border border-white/10 bg-[#202020] p-4 flex flex-col gap-3" data-testid={`dash-card-${mod.key}`}>
      <div className="flex items-center gap-2.5">
        <PctRing value={pct} color={mod.color} size={compact ? 32 : 44} />
        <span className="text-sm font-semibold truncate">{mod.label}</span>
        <div className="ml-auto inline-flex rounded-md overflow-hidden border border-white/10" data-testid={`dash-period-${mod.key}`}>
          {PERIODS.map(([k, s, title]) => (
            <button key={k} type="button" title={title} onClick={() => onPeriod(k)}
              className="px-1.5 py-0.5 text-[10px] font-semibold transition-colors"
              style={{ background: period === k ? `${mod.color}33` : "transparent", color: period === k ? mod.color : "#8a8a8f" }}
              data-testid={`dash-period-${mod.key}-${k}`}>{s}</button>
          ))}
        </div>
      </div>
      <div>
        <div className="text-3xl font-bold tabular-nums leading-none" data-testid={`dash-count-${mod.key}`}>{data?.count ?? 0}</div>
        <div className="text-[11px] text-[#8a8a8f] mt-1">{data?.count_label || ""}</div>
      </div>
      <TrendChart series={data?.series} period={period} color={mod.color} id={mod.key} />
      <div className="border-t border-white/10 pt-2 flex items-center justify-between text-[11px]">
        <span className="font-semibold inline-flex items-center gap-1" style={{ color: urgentColor }} data-testid={`dash-urgent-${mod.key}`}>
          <span className="w-1.5 h-1.5 rounded-full" style={{ background: urgentColor }} />{urgent.label}
        </span>
        <span className="inline-flex items-center gap-1 tabular-nums" title="Score de performance (90 j)">
          <span className="text-[10px] uppercase tracking-wider text-[#8a8a8f]">Perf.</span>
          <span className="font-semibold" style={{ color: pct == null ? "#6b6b70" : scoreColor(pct) }}>{pct == null ? "—" : `${pct} %`}</span>
        </span>
      </div>
    </div>
  );
}

export function ComingSoonCard({ mod }) {
  const Icon = mod.icon;
  return (
    <div className="rounded-xl p-4 flex flex-col items-center justify-center gap-2 text-center select-none"
      style={{ border: "1px dashed rgba(255,255,255,0.14)", background: "rgba(255,255,255,0.015)", minHeight: 150, color: "#6b6b70" }}
      data-testid={`dash-card-${mod.key}`}>
      <Icon size={22} strokeWidth={1.6} />
      <span className="text-sm font-semibold">{mod.label}</span>
      <span className="text-[10.5px] uppercase tracking-widest">Bientôt disponible</span>
    </div>
  );
}

const HEALTH_ROWS = [
  { key: "backup_b2", label: "Backup B2", tab: "backup-b2", icon: Cloud }, { key: "nas", label: "NAS Synology", tab: "backup-nas", icon: Server },
  { key: "nas_script", label: "Script NAS", tab: "backup-nas", icon: FileCode }, { key: "email_queue", label: "File emails", tab: "email-prefs", icon: Mail },
  { key: "domain", label: "Domaine & SSL", tab: "domain", icon: Globe }, { key: "server", label: "Serveur & jobs", tab: "health", icon: Cpu },
  { key: "env", label: "Environnement", tab: "backup-b2", icon: Layers }, { key: "user_refs", label: "Références utilisateurs", tab: "users", icon: UserCheck },
  { key: "data_coherence", label: "Cohérence des données", tab: "health", icon: DatabaseZap },
];
const LEVELS = {
  green: { color: "#2A9A7A", label: "Normal", bg: "#12201c", fg: "#2A9A7A", Icon: CircleCheck, icon_color: "#4fc39e" },
  amber: { color: "#F9A826", label: "Alerte", bg: "#2a2114", fg: "#F9A826", Icon: TriangleAlert, icon_color: "#E0822E" },
  red: { color: "#e24b4a", label: "Anomalie", bg: "#2a1216", fg: "#f0a9a7", Icon: OctagonAlert, icon_color: "#e24b4a" },
};

/** Bandeau de synthèse : agrège toutes les anomalies (lignes santé + sondes domaine + erreurs serveur / jobs). */
function HealthBanner({ banner, onSee }) {
  const n = banner?.count || 0;
  const red = n > 0;
  const top = (banner?.anomalies || []).slice(0, 2);
  const Icon = red ? AlertTriangle : CheckCircle2;
  const c = red ? { fg: "#f0a9a7", bg: "#2a1216", bd: "#5a2328" } : { fg: "#4fc39e", bg: "#0f1f1a", bd: "#1e4a3d" };
  return (
    <div className="flex items-center shrink-0" style={{ gap: 8, padding: "7px 10px", borderRadius: 8, background: c.bg, border: `1px solid ${c.bd}`, color: c.fg }}
      data-testid="dash-health-banner" data-state={red ? "red" : "green"}>
      <Icon size={15} style={{ flexShrink: 0 }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div className="font-semibold" style={{ fontSize: 12 }} data-testid="dash-health-banner-title">
          {red ? `${n} anomalie${n > 1 ? "s" : ""} détectée${n > 1 ? "s" : ""}` : "Aucune anomalie · tout opérationnel"}
        </div>
        {red && <div className="truncate" style={{ fontSize: 10.5, opacity: 0.85 }} data-testid="dash-health-banner-summary">
          {top.map((a) => `${a.label} — ${a.summary}`).join(" · ")}
        </div>}
      </div>
      {red && (
        <button type="button" onClick={() => onSee(top[0])} className="font-semibold uppercase hover:brightness-125 transition-all"
          style={{ fontSize: 9.5, letterSpacing: ".04em", padding: "3px 9px", borderRadius: 5, background: "rgba(240,169,167,0.14)", border: `1px solid ${c.bd}`, color: c.fg, flexShrink: 0 }}
          data-testid="dash-health-banner-see">Voir</button>
      )}
    </div>
  );
}

export function SystemHealthCard({ health }) {  // remplit la hauteur restante de la colonne droite — jamais de scroll
  const navigate = useNavigate();
  const listRef = useRef(null);
  const [compact, setCompact] = useState(false);
  useEffect(() => { // compactage auto (hystérésis : compact < 44 px/ligne, décompact ≥ 48 px) — plancher police 10 px
    const el = listRef.current; if (!el) return;
    const ro = new ResizeObserver(([e]) => {
      const per = (e.contentRect.height - 6 * (HEALTH_ROWS.length - 1)) / HEALTH_ROWS.length;
      setCompact((c) => (c ? per < 48 : per < 44));
    });
    ro.observe(el); return () => ro.disconnect();
  }, [health]);
  if (!health) return <SystemHealthSkeleton />;
  const copy = (e, r, h, lvl) => {
    e.stopPropagation();
    const when = h.checked_at ? new Date(h.checked_at).toLocaleString("fr-FR", { timeZone: "Africa/Algiers" }) : "—";
    const txt = `[Le Desk · Santé système] ${r.label} — ${lvl.label}\nDétail : ${h.detail || h.summary}\nContrôle effectué le : ${when} (Alger)`;
    navigator.clipboard?.writeText(txt).then(() => toast.success("Message copié")).catch(() => toast.error("Copie impossible"));
  };
  return (
    <section className="flex flex-col min-w-0 h-full min-h-0" style={{ overflow: "hidden" }} data-testid="dash-health">
      <div className="flex items-center gap-2 mb-2 shrink-0">
        <HeartPulse size={15} className="text-[#5dcaa5]" />
        <span className="text-sm font-semibold">Santé système</span>
      </div>
      <div ref={listRef} className="flex flex-col flex-1 min-h-0" style={{ gap: compact ? 4 : 6, overflow: "hidden", justifyContent: "space-between" }} data-testid="dash-health-rows" data-compact={compact || undefined}>
        {HEALTH_ROWS.map((r) => {
          const h = health?.[r.key];
          const lvl = LEVELS[h?.level] || { color: "#55555a", label: "Contrôle en cours", bg: "#1a1a1e", fg: "#8b8b93", Icon: Clock, icon_color: "#55555a" };
          const Icon = r.icon;
          return (
            <div key={r.key} role="button" tabIndex={0} onClick={() => navigate(`/parametres?tab=${r.tab}`)} onKeyDown={(e) => e.key === "Enter" && navigate(`/parametres?tab=${r.tab}`)}
              className="flex cursor-pointer hover:brightness-110 transition-all items-center"
              style={{ gap: 10, padding: compact ? "2px 10px" : "6px 12px", flex: "1 1 0", minHeight: compact ? 22 : 44, overflow: "hidden", background: "#141418", border: "0.5px solid #26262a", borderLeft: `3px solid ${lvl.color}`, borderRadius: 8, transition: "padding .15s ease, min-height .15s ease" }}
              data-testid={`dash-health-${r.key}`} data-level={h?.level || "pending"}>
              <Icon size={15} style={{ color: lvl.color, flexShrink: 0, marginTop: 1 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="flex items-center" style={{ gap: 8 }}>
                  <span className="truncate" title={r.label} style={{ fontSize: compact ? 11.5 : 12, color: "#e8e8ec", flex: compact ? "0 0 auto" : 1, minWidth: 0 }}>{r.label}</span>
                  {compact && <span className="truncate" title={h?.detail || ""} style={{ fontSize: 10, color: "#8b8b93", flex: 1, minWidth: 0 }} data-testid={`dash-health-detail-${r.key}`}>{h?.detail || h?.summary || "Contrôle en cours…"}</span>}
                  {h && h.level !== "green" && (
                    <button type="button" onClick={(e) => copy(e, r, h, lvl)} className="hover:text-white transition-colors" style={{ color: "#6e6e78", flexShrink: 0 }} title="Copier le message technique"
                      data-testid={`dash-health-copy-${r.key}`}><Copy size={13} /></button>
                  )}
                </div>
                {!compact && <div className="truncate" style={{ fontSize: 10.5, color: "#8b8b93", marginTop: 2, lineHeight: 1.3 }} data-testid={`dash-health-detail-${r.key}`}>{h?.detail || h?.summary || "Contrôle en cours…"}</div>}
              </div>
              <span role="img" title={lvl.label} aria-label={lvl.label} className="inline-flex items-center self-center" style={{ flexShrink: 0, color: lvl.icon_color }}
                data-testid={`dash-health-badge-${r.key}`} data-level={h?.level || "green"}><lvl.Icon size={19} strokeWidth={1.9} /></span>
            </div>
          );
        })}
      </div>
    </section>
  );
}

const COLS = [
  { key: "module", label: "Module" }, { key: "reference", label: "Réf." },
  { key: "created_at", label: "Créé le" }, { key: "modified_at", label: "Modifs" },
  { key: "title", label: "Titre / Objet" }, { key: "status_key", label: "Statut" },
  { key: "project_name", label: "Siège / Projet", align: "center" }, { key: "reason", label: "Motif" },
  { key: "attachments", label: "PJ", align: "center" }, { key: "since_days", label: "Depuis", align: "center" },
];
const GRID = "88px 78px 68px 68px minmax(180px,3fr) 90px 66px minmax(140px,2fr) 44px 52px";
const fmtCreated = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "2-digit" });
};

function Initials({ id, name, size = 24 }) {
  const c = avatarColorForSeed(id);
  const parts = (name || "?").split(" ");
  const ini = `${parts[0]?.charAt(0) || "?"}${parts[1]?.charAt(0) || ""}`.toUpperCase();
  return (
    <span className="inline-flex items-center justify-center rounded-full font-semibold shrink-0"
      style={{ width: size, height: size, background: c.bg, color: c.fg, fontSize: Math.round(size * 0.42) }}>{ini}</span>
  );
}

function Avatar({ id, name, size = 34 }) {
  const { byId } = useUserDirectory();
  const parts = (name || "").split(" ");
  const u = byId[id] || { id, first_name: parts[0] || "?", last_name: parts.slice(1).join(" ") };
  return <UserAvatar user={u} size={size} showPopover={false} className="shrink-0" />;
}

function AvatarRing({ id, name, ratio, color, size = 36 }) {
  const c = avatarColorForSeed(id);
  const parts = (name || "?").split(" ");
  const ini = `${parts[0]?.charAt(0) || "?"}${parts[1]?.charAt(0) || ""}`.toUpperCase();
  const r = (size - 4) / 2, circ = 2 * Math.PI * r;
  return (
    <span className="relative inline-flex items-center justify-center shrink-0" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="absolute inset-0 -rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="3" />
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth="3" strokeLinecap="round"
          strokeDasharray={circ} strokeDashoffset={circ * (1 - Math.max(0, Math.min(1, ratio)))} style={{ transition: "stroke-dashoffset .4s" }} />
      </svg>
      <span className="inline-flex items-center justify-center rounded-full font-semibold"
        style={{ width: size - 10, height: size - 10, background: c.bg, color: c.fg, fontSize: Math.round(size * 0.3) }}>{ini}</span>
    </span>
  );
}

function Skel({ w = "100%", h = 12, r = 6, className = "" }) {
  return <div className={`desk-skeleton ${className}`} style={{ width: w, height: h, borderRadius: r }} aria-hidden="true" />;
}

/** Spec iter327 — squelettes à dimensions finales (aucun décalage à l'arrivée des données). */
export function PeopleRankingSkeleton() {
  return (
    <section className="rounded-xl border border-white/10 bg-[#202020] flex flex-col shrink-0 min-w-0" style={{ padding: "10px 16px" }} data-testid="dash-people-skeleton">
      <div className="flex items-center gap-2 mb-2"><Skel w={15} h={15} r={4} /><Skel w={140} h={13} /></div>
      <div className="flex items-center justify-center" style={{ gap: 18, padding: "2px 2px 6px" }}>
        {Array.from({ length: 4 }).map((_, i) => <Skel key={i} w={52} h={52} r={26} />)}
      </div>
    </section>
  );
}

export function SystemHealthSkeleton() {
  return (
    <section className="rounded-xl border border-white/10 bg-[#202020] p-4 flex flex-col shrink-0 min-w-0" data-testid="dash-health-skeleton">
      <div className="flex items-center gap-2 mb-1"><Skel w={15} h={15} r={4} /><Skel w={110} h={13} /></div>
      {HEALTH_ROWS.map((r) => (
        <div key={r.key} className="flex" style={{ gap: 10, padding: "10px 0", borderTop: "0.5px solid #222226" }}>
          <Skel w={3} h={34} r={2} /><Skel w={15} h={15} r={4} className="mt-px" />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="flex items-center" style={{ gap: 8 }}><Skel w={90} h={12} /><span style={{ flex: 1 }} /><Skel w={54} h={16} r={4} /></div>
            <Skel w="85%" h={10} className="mt-1.5" />
          </div>
        </div>
      ))}
    </section>
  );
}

const PERSON_MODULES = [["taches", "Tâches", "#D99B1E"], ["documents", "Documents", "#3568B0"], ["accuses", "Accusé", "#F9A826"], ["validations", "Validation", "#5B5FC7"], ["information", "Information", "#2A9A7A"]];
const totalColor = (n) => (n > 50 ? "#DC2626" : n > 0 ? "#F9A826" : "#2A9A7A");

/** Étiquette de détail (survol / clic) : nom · N à faire, barre de répartition par module, petites étiquettes cliquables (modules > 0). */
function PersonTip({ p, onPickModule, perf }) {
  const mods = PERSON_MODULES.map(([k, label, color]) => ({ k, label, color, n: p.by_module?.[k] || 0 })).filter((m) => m.n > 0).sort((a, b) => b.n - a.n);
  if (perf) return (
    <div data-testid={`dash-people-tipcard-${p.user_id}`}>
      <PerformanceCard perf={perf} title={`Performance — ${p.name}`} />
      <div style={{ width: 300, padding: "8px 14px 2px", fontSize: 11, color: "#8b8b93" }}>{p.count ? `${p.count} action(s) à faire` : "À jour"}{mods.length > 0 && <span className="inline-flex flex-wrap" style={{ gap: 4, marginLeft: 8 }}>{mods.map((m) => <button key={m.k} type="button" onClick={(e) => { e.stopPropagation(); onPickModule?.(p.user_id, m.k); }} style={{ fontSize: 9.5, padding: "1px 6px", borderRadius: 5, background: `${m.color}22`, color: m.color, border: `1px solid ${m.color}55` }} data-testid={`dash-people-tip-${p.user_id}-${m.k}`}>{m.label} {m.n}</button>)}</span>}</div>
    </div>
  );
  return (
    <div style={{ width: 176 }} data-testid={`dash-people-tipcard-${p.user_id}`}>
      <div style={{ fontSize: 11, color: "#e6e6ea", fontWeight: 500 }}>{p.name} <span style={{ color: "#8b8b93", fontWeight: 400 }}>· {p.count ? `${p.count} à faire` : "à jour"}</span></div>
      {mods.length > 0 && (<>
        <div className="flex" style={{ height: 6, borderRadius: 3, overflow: "hidden", background: "#26262a", margin: "7px 0" }} aria-hidden="true">
          {mods.map((m) => <span key={m.k} style={{ flex: m.n, background: m.color }} />)}
        </div>
        <div className="flex flex-wrap" style={{ gap: 4 }}>
          {mods.map((m) => <button key={m.k} type="button" onClick={(e) => { e.stopPropagation(); onPickModule?.(p.user_id, m.k); }} className="inline-flex items-center" style={{ gap: 4, fontSize: 9.5, padding: "2px 6px", borderRadius: 5, background: `${m.color}22`, color: m.color }} data-testid={`dash-people-tip-${p.user_id}-${m.k}`}>{m.label} <b style={{ fontWeight: 600 }}>{m.n}</b></button>)}
        </div>
      </>)}
    </div>
  );
}

const contrastText = (bg) => (bg === "#F9A826" ? "#1b1b1b" : "#fff");

const RING_CSS = `@keyframes dash-ring{from{stroke-dashoffset:var(--c)}to{stroke-dashoffset:var(--o)}}.dash-ring-arc{animation:dash-ring 1.2s cubic-bezier(.33,1,.68,1) both}`;
/** Portrait (chantier 3) : anneau SVG 56 px (stroke 2,5 · piste #26262c · bouts ronds · animation 1,2 s) coloré par seuils d'indice autour de l'avatar 40 px ; pastille du total ; prénom + indice % dessous. */
function PersonBadge({ p, active = false, score = null }) {
  const c = totalColor(p.count);
  const r = 26.75, circ = 2 * Math.PI * r, pct = Math.max(0, Math.min(100, score ?? 0));
  const ringColor = score === null ? "#3a3a42" : scoreColor(score);
  const first = (p.name || "").split(" ")[0];
  return (
    <span className="inline-flex flex-col items-center" style={{ gap: 4 }} data-active={active} data-testid={`dash-people-badge-${p.user_id}`} data-score={score ?? ""}>
      <span className="relative inline-flex items-center justify-center" style={{ width: 56, height: 56, borderRadius: "50%", boxShadow: active ? `0 0 0 3px #0e0e10, 0 0 0 5px ${c}` : "none", transition: "box-shadow .2s" }}>
        <svg width={56} height={56} viewBox="0 0 56 56" className="absolute inset-0 -rotate-90" aria-hidden="true">
          <circle cx="28" cy="28" r={r} fill="none" stroke="#26262c" strokeWidth="2.5" />
          <circle className="dash-ring-arc" cx="28" cy="28" r={r} fill="none" stroke={ringColor} strokeWidth="2.5" strokeLinecap="round" strokeDasharray={circ} style={{ "--c": circ, "--o": circ * (1 - pct / 100), strokeDashoffset: circ * (1 - pct / 100) }} data-testid={`dash-people-ring-${p.user_id}`} />
        </svg>
        <Avatar id={p.user_id} name={p.name} size={40} />
        <span className="absolute inline-flex items-center justify-center tabular-nums" style={{ right: -8, bottom: -2, borderRadius: 9, padding: "1px 7px", background: c, color: contrastText(c), fontSize: 11, fontWeight: 500, border: "2px solid #0e0e10" }} data-testid={`dash-people-count-${p.user_id}`}>{p.count}</span>
      </span>
      <span className="flex flex-col items-center leading-tight" style={{ maxWidth: 72 }}>
        <span className="truncate" style={{ fontSize: 10.5, color: "#d6d6dc", maxWidth: 72 }}>{first}</span>
        <span className="tabular-nums" style={{ fontSize: 10, fontWeight: 700, color: ringColor }} data-testid={`dash-people-score-${p.user_id}`}>{score === null ? "—" : `${score} %`}</span>
      </span>
    </span>
  );
}

/** « Action par personne » — deux lignes d'avatars (sans défilement), séparateur en dégradé, étiquette détaillée au survol / clic. */
export function PeopleRanking({ people = [], onPickModule, selectedId = null, onSelect }) {
  const [perfs, setPerfs] = useState({});
  useEffect(() => { let alive = true; api.get("/dashboard/team-performance").then(({ data }) => { if (alive) setPerfs(data.items || {}); }).catch(() => {}); return () => { alive = false; }; }, []);
  if (people === null) return <PeopleRankingSkeleton />;
  const renderPerson = (p) => { const active = selectedId === p.user_id; const perf = perfs[p.user_id]; return (
    <Tooltip key={p.user_id}>
      <TooltipTrigger asChild>
        <button type="button" onClick={() => onSelect?.(active ? null : p)} className="focus:outline-none" data-testid={`dash-people-row-${p.user_id}`} aria-pressed={active} aria-label={`${p.name} · ${p.count} à faire`} title={active ? "Retirer le filtre" : `Filtrer sur ${p.name}`}><PersonBadge p={p} active={active} score={perf ? perf.global_score : null} /></button>
      </TooltipTrigger>
      <TooltipContent side="bottom" sideOffset={6} className="rounded-lg" style={{ padding: "8px 10px", pointerEvents: "auto" }}>
        <PersonTip p={p} onPickModule={onPickModule} perf={perf} />
        <TooltipArrow width={10} height={5} style={{ fill: "#1c1c20" }} />
      </TooltipContent>
    </Tooltip>); };
  return (
    <TooltipProvider delayDuration={200}>
    <section className="rounded-xl border border-white/10 bg-[#202020] flex flex-col shrink-0 min-w-0" style={{ padding: "10px 16px" }} data-testid="dash-people">
      <style>{RING_CSS}</style>
      <div className="flex items-center gap-2 mb-2 shrink-0">
        <Users size={15} className="text-[#f0a868]" />
        <span className="text-sm font-semibold">Action par personne</span>
        <span className="ml-auto tabular-nums" style={{ fontSize: 10.5, color: "#8b8b93" }} data-testid="dash-people-count">{people.length} actif{people.length > 1 ? "s" : ""}</span>
      </div>
      <div className="grid justify-items-center" style={{ gridTemplateColumns: "repeat(6, minmax(0, 1fr))", gap: "10px 8px", padding: "2px 2px 6px", overflow: "hidden" }} data-testid="dash-people-body">
        {people.length === 0 && <div className="w-full py-6 text-center text-xs text-[#8a8a8f] italic">Aucun compte actif.</div>}
        {people.map(renderPerson)}
      </div>
    </section>
    </TooltipProvider>
  );
}

const STATUS_COLORS = {
  late: "#e07a7a", redo: "#F97316", to_verify: "#A855F7", delai_depasse: "#EF4444",
  en_cours: "#F97316", nouveau: "#38BDF8", pending: "#8a8a8f",
};
const Pill = ({ color, children, testId, title }) => (
  <span className="inline-flex items-center h-[17px] px-1.5 rounded text-[9.5px] font-semibold leading-none whitespace-nowrap max-w-full truncate"
    style={{ color, background: `${color}22`, border: `1px solid ${color}55` }} data-testid={testId} title={title}>{children}</span>
);

export function AttentionTable({ items = [], byModule = {}, moduleFilter = null, onModuleChange, viewIsDefault = true, onReset, person = null, onClearPerson }) {
  const [modFilter, setModFilter] = useState(moduleFilter?.key || "all");
  useEffect(() => { if (moduleFilter) setModFilter(moduleFilter.key); }, [moduleFilter]);
  useEffect(() => { onModuleChange?.(modFilter); }, [modFilter, onModuleChange]);
  const [sort, setSort] = useState({ key: "since_days", dir: "desc" });
  const [colFilter, setColFilter] = useState({});
  const [openCol, setOpenCol] = useState(null);
  const [quick, setQuick] = useState(null);  // spec iter308 — aperçu rapide { kind, id }
  const openQuick = (r) => setQuick({ kind: r.module === "taches" ? "task" : r.module === "documents" ? "document" : "avi", id: r.id });

  const rows = useMemo(() => {
    let r = items.filter((i) => modFilter === "all" || i.module === modFilter);
    for (const [k, v] of Object.entries(colFilter)) {
      if (!v) continue;
      const q = v.toLowerCase();
      r = r.filter((i) => String(k === "module" ? (DASH_MODULES.find((m) => m.key === i.module)?.label || "")
        : k === "created_at" || k === "modified_at" ? fmtCreated(i[k]) : k === "status_key" ? (i.status_label || "") : (i[k] ?? "")).toLowerCase().includes(q));
    }
    const dir = sort.dir === "asc" ? 1 : -1;
    return [...r].sort((a, b) => {
      const x = a[sort.key] ?? "", y = b[sort.key] ?? "";
      return (typeof x === "number" ? x - y : String(x).localeCompare(String(y), "fr")) * dir;
    });
  }, [items, modFilter, colFilter, sort]);

  const toggleSort = (key) => setSort((s) => ({ key, dir: s.key === key && s.dir === "desc" ? "asc" : "desc" }));
  const just = (c) => (c.align === "center" ? "justify-center text-center" : c.align === "right" ? "justify-end text-right" : "");

  return (
    <section className="rounded-xl border border-white/10 bg-[#202020] h-full flex flex-col" data-testid="dash-attention">
      <div className="flex flex-wrap items-center gap-2 px-4 pt-3 pb-2">
        <AlertTriangle size={15} className="text-[#f87171]" />
        <span className="text-sm font-semibold" data-testid="dash-attention-title">Action requise · {items.length}</span>
        <div className="ml-auto flex flex-wrap gap-1.5" data-testid="dash-attention-filters">
          {[{ key: "all", label: "Tout" }, ...DASH_MODULES].map((m) => {
            const active = modFilter === m.key;
            const n = m.key === "all" ? items.length : (byModule[m.key] || 0);
            const Icon = m.icon;
            const isAll = m.key === "all";
            return (
              <button key={m.key} type="button" onClick={() => setModFilter(m.key)}
                className="inline-flex items-center gap-1.5 rounded-full text-[11px] font-medium transition-all"
                style={{ padding: "4px 11px", background: isAll ? "#e24b4a" : (active ? `${m.color}1A` : "transparent"),
                  color: isAll ? "#fff" : m.color, border: `1px solid ${isAll ? "#e24b4a" : m.color}`,
                  opacity: active || modFilter === "all" ? 1 : 0.5 }}
                data-testid={`dash-filter-${m.key}`}>{Icon && <Icon size={12} />}{m.label} · {n}</button>
            );
          })}
          {COMING.map((c) => (
            <span key={c.key} className="rounded-full text-[11px] border border-dashed border-white/15 text-[#6b6b70] cursor-not-allowed select-none"
              style={{ padding: "4px 11px" }} data-testid={`dash-filter-${c.key}`}>{c.label} · 0</span>
          ))}
          {(modFilter !== "all" || !viewIsDefault) && (
            <button type="button" onClick={() => { setModFilter("all"); onReset?.(); }}
              className="inline-flex items-center justify-center w-[26px] h-[26px] rounded-full border border-white/15 text-[#c8c8cc] hover:bg-white/10 hover:text-white transition-colors"
              title="Réinitialiser : Ma vue · Tout" data-testid="dash-filter-reset"><X size={13} /></button>
          )}
        </div>
      </div>
      {person && (
        <div className="mx-4 mb-2 flex items-center gap-2 rounded-md px-3 py-1.5 text-[11px]" style={{ background: "#1e1a2e", color: "#a8a6e8", border: "0.5px solid #2e2a44" }} data-testid="dash-person-filter">
          <Users size={12} />Filtré sur : <b style={{ fontWeight: 600, color: "#e8e8ec" }} data-testid="dash-person-filter-name">{person.name}</b>
          <button type="button" onClick={onClearPerson} className="ml-auto inline-flex items-center justify-center w-[20px] h-[20px] rounded-full hover:bg-white/10 transition-colors" title="Retirer le filtre" aria-label="Retirer le filtre" data-testid="dash-person-filter-clear"><X size={12} /></button>
        </div>
      )}
      <div className="px-4 pb-3 flex-1 flex flex-col min-h-0">
        <div className="grid items-center gap-x-2 text-[10px] uppercase text-[#8a8a8f] border-b border-white/10 pb-1.5"
          style={{ gridTemplateColumns: GRID, fontWeight: 500, letterSpacing: "0.06em", paddingRight: 16 }}>
          {COLS.map((c) => {
            const sorted = sort.key === c.key;
            return (
              <div key={c.key} className={`relative flex items-center gap-0.5 min-w-0 ${just(c)}`} data-testid={`dash-col-${c.key}`}>
                <button type="button" onClick={() => toggleSort(c.key)} className="hover:text-white transition-colors truncate"
                  style={{ color: sorted ? "#0078D4" : undefined, fontWeight: 500 }}>{c.label}</button>
                <button type="button" onClick={() => setOpenCol(openCol === c.key ? null : c.key)} title="Filtrer"
                  style={{ color: sorted ? "#0078D4" : undefined }} data-testid={`dash-col-filter-${c.key}`}>
                  <ChevronDown size={10} />
                </button>
                {colFilter[c.key] && <span className="w-1.5 h-1.5 rounded-full bg-[#0078D4]" />}
                {openCol === c.key && (
                  <input autoFocus value={colFilter[c.key] || ""} onChange={(e) => setColFilter({ ...colFilter, [c.key]: e.target.value })}
                    onBlur={() => setOpenCol(null)} placeholder={`Filtrer ${c.label.toLowerCase()}…`}
                    className="absolute left-0 top-6 z-20 w-44 rounded border border-white/15 bg-[#161616] px-2 py-1 text-[11px] normal-case tracking-normal text-white"
                    data-testid={`dash-col-input-${c.key}`} />
                )}
              </div>
            );
          })}
        </div>
        <div className="overflow-y-auto overflow-x-hidden" style={{ flex: "1 1 0", minHeight: 0, paddingRight: 6, scrollbarGutter: "stable" }} data-testid="dash-attention-body">
          {rows.length === 0 && <div className="py-6 text-center text-xs text-[#8a8a8f] italic">Rien ne requiert votre action.</div>}
          {rows.map((r) => {
            const m = DASH_MODULES.find((x) => x.key === r.module) || DASH_MODULES[0];
            const pj = r.attachments || 0;
            const sc = STATUS_COLORS[r.status_key] || "#8a8a8f";
            return (
              <div key={`${r.module}-${r.id}`} role="link" tabIndex={0} onClick={() => openQuick(r)}
                onKeyDown={(e) => e.key === "Enter" && openQuick(r)} title="Aperçu rapide"
                className="grid items-center gap-x-2 text-[11.5px] border-b border-[#1c1c1e] cursor-pointer hover:bg-white/[0.04] transition-colors"
                style={{ gridTemplateColumns: GRID, minHeight: 32 }} data-testid={`dash-attention-row-${r.id}`}>
                <span className="flex items-center min-w-0"><Pill color={m.color}>{m.label}</Pill></span>
                <span className="font-semibold tabular-nums truncate" style={{ color: m.ref }}>{r.reference || "—"}</span>
                <span className="tabular-nums text-[#c8c8cc] truncate" data-testid={`dash-attention-created-${r.id}`}>{fmtCreated(r.created_at)}</span>
                <span className="tabular-nums text-[#c8c8cc] truncate" data-testid={`dash-attention-modified-${r.id}`}>{fmtCreated(r.modified_at)}</span>
                <span className="flex items-center gap-1.5 min-w-0">
                  <span className="truncate text-white" title={r.title || ""}>{r.title || "—"}</span>
                  {r.module === "documents" && r.type_name && (
                    <span className="inline-flex items-center h-[16px] px-1.5 rounded text-[9px] font-bold uppercase tracking-wide whitespace-nowrap shrink-0"
                      style={{ color: r.type_color || "#7bb3e8", background: `${r.type_color || "#7bb3e8"}1F`, border: `1px solid ${r.type_color || "#7bb3e8"}47` }}
                      data-testid={`dash-attention-type-${r.id}`}>{r.type_name}</span>
                  )}
                </span>
                <span className="flex items-center min-w-0"><Pill color={sc} testId={`dash-attention-status-${r.id}`}>{r.status_label || "—"}</Pill></span>
                <span className="truncate text-[#c8c8cc] text-center" title={r.project_name || ""} data-testid={`dash-attention-project-${r.id}`}>{r.project_name || "—"}</span>
                <span className="flex items-center min-w-0"><Pill color={m.color} title={r.reason}>{r.reason || "—"}</Pill></span>
                <span className="inline-flex items-center justify-center gap-0.5 tabular-nums" data-testid={`dash-attention-pj-${r.id}`}
                  style={{ color: pj ? "#c8c8cc" : "#55555a" }} title={pj ? `${pj} pièce${pj > 1 ? "s" : ""} jointe${pj > 1 ? "s" : ""}` : "Aucune pièce jointe"}>
                  <Paperclip size={10} />{pj || "—"}
                </span>
                <span className="font-bold tabular-nums text-center" style={{ color: r.critical ? "#f87171" : "#f0a868" }}>{r.since_days} j</span>
              </div>
            );
          })}
        </div>
      </div>
      {quick && <QuickViewDialog kind={quick.kind} id={quick.id} onClose={() => setQuick(null)} />}
    </section>
  );
}
